import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { workpapersAPI, WORKPAPER_STATUS_LABELS, WORKPAPER_STATUS_COLORS } from '../lib/workpapersAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';

export default function Workpapers() {
  const navigate = useNavigate();
  const [wps, setWps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ client_id: '', template_key: '', financial_year: '' });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [wpRes, tplRes, clRes] = await Promise.all([
        workpapersAPI.list(),
        workpapersAPI.templates(),
        clientsAPI.list({ status: 'active' }),
      ]);
      setWps(wpRes.data);
      setTemplates(tplRes.data);
      setClients(clRes.data);
    } catch (error) {
      toast.error('Failed to load workpapers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.template_key) {
      toast.error('Client and template are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await workpapersAPI.create({
        client_id: form.client_id,
        template_key: form.template_key,
        financial_year: form.financial_year || null,
      });
      toast.success('Workpaper created');
      setDialogOpen(false);
      navigate(`/workpapers/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded"></div></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="workpapers-title">
            Workpapers
          </h1>
          <p className="text-muted-foreground mt-1">Structured working papers with reviewer → partner sign-off</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="new-workpaper-button">
              <Plus className="w-4 h-4 mr-2" /> New Workpaper
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Workpaper</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="wp-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Template *</Label>
                <Select value={form.template_key} onValueChange={(v) => setForm({ ...form, template_key: v })}>
                  <SelectTrigger data-testid="wp-template-select"><SelectValue placeholder="Select workpaper type" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.name} ({t.item_count} items)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Financial year</Label>
                <Input placeholder="e.g. FY2025" value={form.financial_year} onChange={(e) => setForm({ ...form, financial_year: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {wps.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16 text-muted-foreground">
            <FileCheck className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p>No workpapers yet</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Workpaper</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">FY</th>
                    <th className="px-4 py-3 font-medium">Progress</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {wps.map((w) => {
                    const done = (w.items || []).filter((i) => i.completed).length;
                    return (
                      <tr key={w.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/workpapers/${w.id}`)} data-testid={`wp-row-${w.id}`}>
                        <td className="px-4 py-3 font-medium text-foreground">{w.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{w.client_name || '—'}</td>
                        <td className="px-4 py-3 text-muted-foreground">{w.financial_year || '—'}</td>
                        <td className="px-4 py-3 text-muted-foreground">{done}/{(w.items || []).length}</td>
                        <td className="px-4 py-3"><Badge className={`${WORKPAPER_STATUS_COLORS[w.status]} text-xs`}>{WORKPAPER_STATUS_LABELS[w.status]}</Badge></td>
                        <td className="px-4 py-3 text-muted-foreground">{formatAUDate(w.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
